// frontend/src/App.jsx
import React, { useEffect, Component } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Layout from './components/Layout';
import AuthLayout from './components/AuthLayout';
import VideoList from './pages/VideoList';
import VideoDetail from './pages/VideoDetail/VideoDetail';
import ExternalVideoDetail from './pages/ExternalVideoDetail/ExternalVideoDetail';
import VideoEdit from './pages/VideoEdit/VideoEdit';
import SignIn from './pages/SignIn';
import Account from './pages/Account/Account';
import ForgotPassword from './pages/ForgotPassword';
import MyVideoList from './pages/MyVideoList';
import MyFavoriteList from './pages/MyFavoriteList';
import UserManagement from './pages/UserManagement';
import UserDetail from './pages/UserDetail';
import ErrorPage from './pages/ErrorPage';
import { SnackbarProvider, useSnackbar } from './contexts/SnackbarProvider';
import { SnackbarType } from './components/Snackbar';
import { DownloadProgressProvider } from './contexts/DownloadProgressProvider';
import { UploadProgressProvider } from './contexts/UploadProgressProvider/UploadProgressProvider';
import { AuthProvider } from './contexts/AuthProvider/AuthProvider';
import { useAuth } from './contexts/AuthProvider';
// axios インターセプターをインポートすると自動的にフックが設定される（副作用インポート）
import './utils/axiosInterceptor';
import AdminNotifications from './pages/AdminNotifications/AdminNotifications';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import PasswordChangeGuard from './components/auth/PasswordChangeGuard';
import { TermsAgreementGuard } from './components/TermsOfService';
import LiveStreamList from './pages/LiveStreamList/LiveStreamList';
import LiveStreamDetail from './pages/LiveStreamDetail/LiveStreamDetail';
import PwaManager from './components/PwaManager/PwaManager';
import PwaErrorBoundary from './components/PwaManager/PwaErrorBoundary';
import { clearBrowsingCaches } from './utils/pwa';

// ResizeObserverエラーを抑制するグローバル関数
const suppressResizeObserverErrors = () => {
  const resizeObserverErrorHandler = error => {
    if (
      error?.message?.includes('ResizeObserver') ||
      (error?.error?.message && error?.error?.message.includes('ResizeObserver'))
    ) {
      // ResizeObserverエラーを抑制
      error.stopImmediatePropagation();
      console.log('Global ResizeObserver error suppressed');
      return true;
    }
    return false;
  };

  // エラーイベントに対するハンドラ
  window.addEventListener('error', event => {
    resizeObserverErrorHandler(event);
  }, true);

  // Promiseの拒否イベントに対するハンドラ
  window.addEventListener('unhandledrejection', event => {
    resizeObserverErrorHandler(event);
  });
};

// 認証エラー発生時にリダイレクトするラッパーコンポーネント
const AppRoutes = () => {
  const { currentUser, setCurrentUser, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation(); // 現在のパス情報を取得
  const showSnackbar = useSnackbar();

  // 認証関連イベントハンドラーの設定
  useEffect(() => {
    // セッション切れの場合の処理 (unauthorizedイベント)
    const handleUnauthorized = () => {
      // 現在のユーザー情報をクリア
      setCurrentUser(null);

      // ログインページにいない場合のみ、セッション切れメッセージを表示
      if (location.pathname !== '/login') {
        showSnackbar('セッションが切れたため再ログインが必要です。', SnackbarType.WARNING);
      }

      // ログインページへリダイレクト
      navigate('/login');
    };

    // 通常のログアウト時の処理 (logoutイベント)
    const handleLogout = () => {
      // Service Worker が貯めた閲覧 URL のキャッシュを消す。
      // Cache Storage はブラウザの「閲覧履歴の削除」では消えないため、
      // 共有端末で次の利用者に前の利用者の検索語・動画 ID が残らないようにする。
      void clearBrowsingCaches();

      // 現在のユーザー情報をクリア
      setCurrentUser(null);

      // ログアウト成功メッセージを表示
      showSnackbar('ログアウトしました', SnackbarType.SUCCESS);

      // ログインページへリダイレクト
      navigate('/login');
    };

    // ログイン成功時の処理 (loginイベント)
    const handleLogin = () => {

    };

    // システムエラー時の処理 (system-errorイベント)
    const handleSystemError = (event) => {

      // 現在すでにエラーページにいる場合は何もしない
      if (location.pathname === '/error') {
        return;
      }

      // ページ遷移が失敗する可能性を考慮してtry-catchで囲む
      try {
        navigate('/error');
      } catch (e) {
        // フォールバックとして直接URLを変更
        window.location.href = '/error';
      }
    };

    // イベントリスナーの登録
    window.addEventListener('unauthorized', handleUnauthorized);
    window.addEventListener('logout', handleLogout);
    window.addEventListener('login', handleLogin);
    window.addEventListener('system-error', handleSystemError);

    // クリーンアップ関数
    return () => {
      window.removeEventListener('unauthorized', handleUnauthorized);
      window.removeEventListener('logout', handleLogout);
      window.removeEventListener('login', handleLogin);
      window.removeEventListener('system-error', handleSystemError);
    };
  }, [navigate, setCurrentUser, showSnackbar, location.pathname]);

  // ログイン必須のルートラッパー
  const ProtectedRoute = ({ children, roles }) => {
    if (loading) {
      return <div>Loading...</div>;
    }
    if (!currentUser) {
      return <Navigate to="/login" />;
    }
    if (roles && !roles.includes(currentUser.role)) {
      showSnackbar('権限が必要です。', SnackbarType.ERROR);
      return <Navigate to="/" />;
    }
    return children;
  };

  // // Admin専用のルートラッパー
  // const AdminOnlyRoute = ({ children }) => {
  //   if (loading) {
  //     return <div>Loading...</div>;
  //   }
  //   if (!currentUser) {
  //     return <Navigate to="/login" />;
  //   }
  //   if (currentUser.role !== 'admin') {
  //     showSnackbar('管理者権限が必要です。', SnackbarType.ERROR);
  //     return <Navigate to="/" />;
  //   }
  //   return children;
  // };

  return (
    <SnackbarProvider>
      <Routes>
        {/* エラーページ */}
        <Route path="/error" element={<ErrorPage />} />

        {/* 認証不要のルート */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<SignIn setCurrentUser={setCurrentUser} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
        </Route>

        {/* 認証必須のルート */}
        <Route element={
          <TermsAgreementGuard>
            <Layout currentUser={currentUser} />
          </TermsAgreementGuard>
        }>
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <VideoList currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/discover"
            element={
              <ProtectedRoute>
                <VideoList currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/videos/:id"
            element={
              <ProtectedRoute>
                <VideoDetail currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/external-videos/:id"
            element={
              <ProtectedRoute>
                <ExternalVideoDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/videos/:id/edit"
            element={
              <ProtectedRoute>
                <VideoEdit currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute>
                <Account currentUser={currentUser} setCurrentUser={setCurrentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-videos"
            element={
              <ProtectedRoute>
                <MyVideoList currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/my-favorites"
            element={
              <ProtectedRoute>
                <MyFavoriteList currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-streams"
            element={
              <ProtectedRoute>
                <LiveStreamList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live-streams/:id"
            element={
              <ProtectedRoute>
                <LiveStreamDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute>
                <UserManagement currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/:id"
            element={
              <ProtectedRoute>
                <UserDetail currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute roles={['admin']}>
                <UserManagement currentUser={currentUser} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/notifications"
            element={
              <ProtectedRoute roles={['admin']}>
                <AdminNotifications />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* その他のパスは "/" へリダイレクト */}
        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </SnackbarProvider>
  );
};

const App = () => {
  // アプリ初期化時にResizeObserverエラー抑制を設定
  useEffect(() => {
    suppressResizeObserverErrors();
  }, []);

  return (
    <>
      {/* PWA の更新トースト・接続エラー案内。ルーティングや認証には依存しない。
          付加機能なので、ここが落ちてもアプリ本体は描画し続ける（フォールバックは null）。 */}
      <PwaErrorBoundary>
        <PwaManager />
      </PwaErrorBoundary>
      <Router>
        <AuthProvider>
          <SnackbarProvider>
            <DownloadProgressProvider>
              <UploadProgressProvider>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </UploadProgressProvider>
            </DownloadProgressProvider>
          </SnackbarProvider>
        </AuthProvider>
      </Router>
    </>
  );
};

export default App;
